// src/api/managedSources.ts

import type { components } from "@verity/shared-types";

import { apiJson } from "./client";
import type { SourceDeleteRequest, SourceDeleteResult } from "./dataSources";
import type {
  ManagedSourceConnector,
  ConnectorRequestResult,
  ManagedSourceSetupPayload,
  ManagedSource,
  ManagedSourceListPage,
  PipelineRun,
  GoogleAnalyticsProperty,
  GoogleAdsCustomer,
  GoogleSearchConsoleSite,
  FacebookAdAccount,
  MicrosoftAdsManager,
  MicrosoftAdsAccount,
  GA4BqExportDataset,
  GA4BqExportDatasetEntry,
  LineageResponse,
  FreshnessMode,
} from "@/types/managedSources";

/** GET /managed-sources/connectors */
export async function listConnectors(): Promise<ManagedSourceConnector[]> {
  return apiJson<ManagedSourceConnector[]>("/managed-sources/connectors");
}

/**
 * POST /managed-sources/connectors/:type/request — ask for a connector that
 * is "Available on request" (ADR-0410). Owner/admin only; asking again while
 * a request is open returns the open one.
 */
export async function requestConnector(
  connectorType: string,
  note?: string
): Promise<ConnectorRequestResult> {
  const trimmed = note?.trim();
  return apiJson<ConnectorRequestResult>(
    `/managed-sources/connectors/${encodeURIComponent(connectorType)}/request`,
    {
      method: "POST",
      body: JSON.stringify(trimmed ? { note: trimmed } : {}),
    }
  );
}

/** POST /managed-sources/setup */
export async function setupManagedSource(
  payload: ManagedSourceSetupPayload
): Promise<ManagedSource> {
  return apiJson<ManagedSource>("/managed-sources/setup", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** GET /managed-sources */
export async function listManagedSources(params?: {
  connector_type?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<ManagedSource[]> {
  const q = new URLSearchParams();
  if (params?.connector_type) q.set("connector_type", params.connector_type);
  if (params?.status) q.set("status", params.status);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  const qs = q.toString();
  return apiJson<ManagedSource[]>(`/managed-sources${qs ? `?${qs}` : ""}`);
}

/** GET /managed-sources — the whole body, not just `data`: the Sources page
 *  also needs `account_transforms` (account-level transforms with no source of
 *  their own, e.g. COMPOSITES). `unwrapData: false` because `apiJson` would
 *  otherwise return `data` alone and drop the sibling field. */
export async function listManagedSourcesPage(): Promise<ManagedSourceListPage> {
  const body = await apiJson<Partial<ManagedSourceListPage>>("/managed-sources", {
    unwrapData: false,
  });
  return {
    data: body?.data ?? [],
    account_transforms: body?.account_transforms ?? [],
  };
}

/** GET /managed-sources/lineage — the account's run-independent model graph
 *  per connector, overlaid with the latest run's per-model statuses. */
export async function fetchAccountLineage(): Promise<LineageResponse> {
  return apiJson<LineageResponse>("/managed-sources/lineage");
}

/** GET /managed-sources/:id */
export async function getManagedSource(id: string): Promise<ManagedSource> {
  return apiJson<ManagedSource>(
    `/managed-sources/${encodeURIComponent(id)}`
  );
}

/** PATCH /managed-sources/:id */
export async function updateManagedSource(
  id: string,
  patch: {
    name?: string;
    schedule_interval?: string;
    schedule_hour?: number;
    schedule_timezone?: string;
    schedule_cron?: string;
    /** Within-day refresh cadence — a 5-field cron read in the source's
     *  `schedule_timezone`, at most 24 fires in any rolling 24 h. An EXPLICIT
     *  `null` turns it off; omitting the key leaves the stored value alone, so
     *  the two are NOT interchangeable. The backend rejects with 400 when the
     *  source is not a TRANSFORM_ONLY GA4 BigQuery Export source, or when its
     *  `freshness_mode` is not `FRESHNESS` — and rejects a switch to
     *  `freshness_mode: "SCHEDULED"` while a cron is still set. */
    intraday_cron?: string | null;
    categories?: string[];
    status?: string;
    lookback_days?: number;
    /** GA4-only: mini-window size in days for the asset's internal backfill loop.
     * Range 1–30. Lower for high-volume properties that hit GA4 sampling.
     * Backend rejects with 400 if applied to non-GA4 pipelines. */
    ga4_mini_window_days?: number;
    /** Brevo-only: opt-in write-back of Verity's email hash onto each contact
     * as a custom attribute. Backend rejects with 400 on non-Brevo sources. */
    verity_id_writeback?: { enabled: boolean; attribute_name?: string };
    /** Turn this managed source into a segmentation source (or turn it off).
     * Only accepted for connectors that support it. Errors the caller must
     * handle: 400 (blocking refusal — e.g. the required data category is off,
     * or another source already provides this connector's profiles), 403 (the
     * caller has no grant on this source), and — on turn-off only — 409 with a
     * `{ detail, conflicting_segments }` body listing the segments still built
     * on it. */
    connector_segmentation_requested?: boolean;
    /** Bring-your-own-data sources only: how new data arrives in this source.
     *  Backend rejects with 400 on sources Verity refreshes itself. */
    freshness_mode?: FreshnessMode;
    /** Bring-your-own-data sources only: the wall-clock "HH:MM" by which this
     *  source's data is usually there. Send `null` to clear it — omitting the
     *  key leaves the stored value alone, so the two are NOT interchangeable.
     *  Backend rejects with 400 on a malformed time or a Verity-refreshed
     *  source. */
    expected_arrival_local_time?: string | null;
    authorization_id?: string;
  }
): Promise<ManagedSource> {
  return apiJson<ManagedSource>(
    `/managed-sources/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
}

/** What the ingest scheduler does with a just-resumed source: a normal sync
 *  (a slot passed while it was paused, or it never ran), the next backfill
 *  chunk, or nothing soon (`null`). */
export type ResumeDueRun = NonNullable<
  components["schemas"]["ManagedSourceUpdateResponse"]["due_run"]
>;

/** PATCH /managed-sources/:id with only `status`: pause or resume.
 *
 *  Reads the whole envelope (`unwrapData: false`): the answer carries
 *  `due_run` next to `data`, and the default unwrap would drop it. */
export async function setManagedSourceStatus(
  id: string,
  status: "ACTIVE" | "PAUSED",
): Promise<{ source: ManagedSource; dueRun: ResumeDueRun | null }> {
  const body = await apiJson<{ data: ManagedSource; due_run?: ResumeDueRun | null }>(
    `/managed-sources/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ status }), unwrapData: false },
  );
  return { source: body.data, dueRun: body.due_run ?? null };
}

/** DELETE /managed-sources/:id. Only for a source nothing of the customer's
 *  hangs off; anything else answers 409 `consent_required`. The drawer deletes
 *  through `DeleteSourceDialog` and `deleteManagedSourceWithConsent`. */
export async function deleteManagedSource(id: string): Promise<void> {
  await apiJson<void>(`/managed-sources/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/**
 * POST /managed-sources/:id/delete, with the consent given on the preview of
 * `getDeleteImpact(source.data_source_id)`. Refusals are the same 409s as
 * `deleteDataSourceWithConsent`.
 */
export async function deleteManagedSourceWithConsent(
  id: string,
  body: SourceDeleteRequest
): Promise<SourceDeleteResult> {
  return apiJson<SourceDeleteResult>(
    `/managed-sources/${encodeURIComponent(id)}/delete`,
    { method: "POST", body: JSON.stringify(body) }
  );
}

/** POST /managed-sources/:id/trigger
 *
 * On a TRANSFORM_ONLY source this route IS "run the transforms", so for an
 * account-level connector it answers the same 202 as `trigger-transform` —
 * narrow with `isTransformScheduled` before reading a source off it. */
export async function triggerPipelineRun(
  id: string
): Promise<ManagedSource | TransformScheduled> {
  return apiJson<ManagedSource | TransformScheduled>(
    `/managed-sources/${encodeURIComponent(id)}/trigger`,
    { method: "POST" }
  );
}

/** POST /managed-sources/:id/support-alert */
export async function fileSupportAlert(
  id: string,
  note?: string
): Promise<{ ok: boolean }> {
  return apiJson<{ ok: boolean }>(
    `/managed-sources/${encodeURIComponent(id)}/support-alert`,
    { method: "POST", body: JSON.stringify({ note }) }
  );
}

/** The 202 shape for account-level connectors: the transform is not started
 * per source but scheduled account-wide — the next sensor tick dispatches the
 * same run the scheduler would (plan 2026-08-04). */
export type TransformScheduled = { status: "SCHEDULED"; message: string };

export function isTransformScheduled(
  r: ManagedSource | TransformScheduled,
): r is TransformScheduled {
  return (r as TransformScheduled).status === "SCHEDULED";
}

/** POST /managed-sources/:id/trigger-transform */
export async function triggerTransformRun(
  id: string,
): Promise<ManagedSource | TransformScheduled> {
  return apiJson<ManagedSource | TransformScheduled>(
    `/managed-sources/${encodeURIComponent(id)}/trigger-transform`,
    { method: "POST" }
  );
}

/** POST /managed-sources/:id/cancel-backfill */
export async function cancelBackfill(id: string): Promise<ManagedSource> {
  return apiJson<ManagedSource>(
    `/managed-sources/${encodeURIComponent(id)}/cancel-backfill`,
    { method: "POST" }
  );
}

/** POST /managed-sources/:id/cancel-run/:runId */
export async function cancelRun(id: string, runId: string): Promise<{ status: string; run_id: string }> {
  return apiJson(
    `/managed-sources/${encodeURIComponent(id)}/cancel-run/${encodeURIComponent(runId)}`,
    { method: "POST" }
  );
}

/** POST /managed-sources/:id/runs/:runId/reconcile */
export async function reconcileRun(
  id: string,
  runId: string
): Promise<{ status: string; changed: boolean; message: string }> {
  return apiJson(
    `/managed-sources/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}/reconcile`,
    { method: "POST" }
  );
}

/** POST /managed-sources/:id/cancel-transform */
export async function cancelTransform(id: string): Promise<{ status: string }> {
  return apiJson(
    `/managed-sources/${encodeURIComponent(id)}/cancel-transform`,
    { method: "POST" }
  );
}

/** POST /managed-sources/:id/trigger-backfill */
export async function triggerBackfillRun(
  id: string,
  params: {
    date_since: string;
    date_until: string;
    categories?: string[];
    // Brevo only: write the Verity ID to EVERY contact this run (not just new
    // ones). One-off; the backend rejects it for non-Brevo / write-back-off.
    verity_id_restamp_all?: boolean;
    // Shopify only: backfill window size in days (1..365). One-off; the backend
    // gates it to Shopify and defaults to 90 when absent.
    shopify_mini_window_days?: number;
  }
): Promise<ManagedSource | TransformScheduled> {
  return apiJson<ManagedSource | TransformScheduled>(
    `/managed-sources/${encodeURIComponent(id)}/trigger-backfill`,
    { method: "POST", body: JSON.stringify(params) }
  );
}

// ---- Source table discovery (transform-only) ----

export type SourceTableDiscoveryResult = {
  available_categories: string[];
  unavailable_categories: {
    key: string;
    label: string;
    missing_tables: string[];
  }[];
  all_tables: string[];
};

/** POST /managed-sources/discover-source-tables */
export async function discoverSourceTables(
  sourceProjectId: string,
  sourceDatasetId: string,
  connectorType: string,
): Promise<SourceTableDiscoveryResult> {
  const resp = await apiJson<{ data: SourceTableDiscoveryResult }>(
    "/managed-sources/discover-source-tables",
    {
      method: "POST",
      body: JSON.stringify({
        source_project_id: sourceProjectId,
        source_dataset_id: sourceDatasetId,
        connector_type: connectorType,
      }),
      unwrapData: false,
    },
  );
  return resp.data;
}

// ---- Source freshness (GA4 BQ Export) ----

/** One bound BigQuery dataset, as the freshness probe last saw it. */
export type SourceFreshnessDataset = {
  dataset_id: string;
  property_id: string;
  /** Newest `events_YYYYMMDD` shard — the last COMPLETE day. */
  latest_event_date: string | null;
  table_count: number;
  /** Newest `events_intraday_YYYYMMDD` shard, or null when this dataset
   *  carries no streaming export. Absent on backends that predate it. */
  latest_intraday_date?: string | null;
  error?: string;
};

/** GET /managed-sources/ga4-bq-export/:id/source-freshness */
export type SourceFreshnessResponse = {
  datasets?: SourceFreshnessDataset[];
  latest_event_date: string | null;
  /** True when ANY bound dataset currently carries an `events_intraday_*`
   *  shard. `undefined` on a backend that does not report it at all — which is
   *  NOT the same statement as "no streaming export" and must not be rendered
   *  as one. */
  streaming_detected?: boolean;
  /** Newest streaming shard across every bound dataset — the "live through"
   *  date, always provisional. */
  latest_intraday_date?: string | null;
  total_table_count?: number;
  table_count?: number;
};

/** GET /managed-sources/ga4-bq-export/:id/source-freshness */
export async function getSourceFreshness(
  id: string,
): Promise<SourceFreshnessResponse> {
  const resp = await apiJson<{ data: SourceFreshnessResponse }>(
    `/managed-sources/ga4-bq-export/${encodeURIComponent(id)}/source-freshness`,
    { unwrapData: false },
  );
  return resp.data;
}

// ---- GA4 BQ Export ----

/** GET /managed-sources/ga4-bq-export/discover-datasets
 *
 * The backend lists the datasets as the runtime service account named on
 * `authorizationId`, so the authorization is required — not optional.
 */
export async function discoverGA4BqDatasets(
  authorizationId: string,
  projectId: string,
  datasetPrefix?: string,
): Promise<{ datasets: GA4BqExportDataset[]; truncated: boolean }> {
  const q = new URLSearchParams({
    authorization_id: authorizationId,
    project_id: projectId,
  });
  if (datasetPrefix) {
    q.set("dataset_prefix", datasetPrefix);
  }
  const resp = await apiJson<{ data: GA4BqExportDataset[]; truncated?: boolean }>(
    `/managed-sources/ga4-bq-export/discover-datasets?${q.toString()}`,
    { unwrapData: false }
  );
  return { datasets: resp.data, truncated: resp.truncated === true };
}

/** PUT /managed-sources/ga4-bq-export/:id/property-labels
 *
 * Labels only: the backend rejects a payload whose dataset/property pairs
 * differ from the stored configuration, so send the source's full list.
 */
export async function updateGA4PropertyLabels(
  id: string,
  sourceDatasets: GA4BqExportDatasetEntry[],
): Promise<GA4BqExportDatasetEntry[]> {
  const resp = await apiJson<{ data: GA4BqExportDatasetEntry[] }>(
    `/managed-sources/ga4-bq-export/${encodeURIComponent(id)}/property-labels`,
    {
      method: "PUT",
      body: JSON.stringify({ source_datasets: sourceDatasets }),
      unwrapData: false,
    },
  );
  return resp.data;
}

/** One custom GA4 event parameter / user property Verity extracts as a column. */
export type GA4CustomParamEntry = { key: string; type: "string" | "number" };

export type GA4CustomParamsConfig = {
  event_params: GA4CustomParamEntry[];
  user_properties: GA4CustomParamEntry[];
};

/** GET /managed-sources/ga4-bq-export/:id/custom-params */
export async function getGA4CustomParams(
  id: string,
): Promise<GA4CustomParamsConfig> {
  const resp = await apiJson<{ data: GA4CustomParamsConfig }>(
    `/managed-sources/ga4-bq-export/${encodeURIComponent(id)}/custom-params`,
    { unwrapData: false },
  );
  return resp.data;
}

/** PUT /managed-sources/ga4-bq-export/:id/custom-params
 *
 * Send the FULL configuration — the backend replaces the stored config with
 * this payload, it does not merge. `message` is set only when the change was
 * saved but no rebuild could be requested; show it instead of the default
 * success text.
 */
export async function updateGA4CustomParams(
  id: string,
  config: GA4CustomParamsConfig,
): Promise<{
  status: string;
  config_changed: boolean;
  backfill_triggered: boolean;
  models: string[];
  message?: string;
}> {
  const resp = await apiJson<{
    data: {
      status: string;
      config_changed: boolean;
      backfill_triggered: boolean;
      models: string[];
      message?: string;
    };
  }>(
    `/managed-sources/ga4-bq-export/${encodeURIComponent(id)}/custom-params`,
    {
      method: "PUT",
      body: JSON.stringify(config),
      unwrapData: false,
    },
  );
  return resp.data;
}

/** One key the property's GA4 export emits, as reported by discovery. */
export type GA4DiscoveredParam = {
  key: string;
  type: "string" | "number";
  occurrences: number;
  /** Who NAMED this key. `ga4_recommended` = Google defines it for one of its
   *  recommended events; the site still chooses to send it, so it stays
   *  selectable, but it is not a field the customer invented. */
  origin: "custom" | "ga4_recommended";
  /** Marts that already carry this key as a column, sorted; empty when nothing
   *  models it. Adding such a key anyway creates a second copy of the value. */
  already_modelled_in: string[];
  /** True when ANY GA4 source in the account already declares this key — the
   *  marts are built from the merged account config, so it is already a column. */
  already_configured: boolean;
};

export type GA4DiscoveredCustomParams = {
  property_id: string;
  dataset_id: string;
  /** Newest export day that was scanned; null when the window held no data. */
  latest_event_date: string | null;
  /** Days ACTUALLY scanned (may be lower than requested — see window_reduced). */
  window_days: number;
  requested_window_days: number;
  window_reduced: boolean;
  /** Keys the export emits that our config format can't express. */
  unsupported_key_count: number;
  event_params: GA4DiscoveredParam[];
  user_properties: GA4DiscoveredParam[];
};

/** GET /managed-sources/ga4-bq-export/:id/discover-custom-params
 *
 * Scans a bounded window of ONE property's raw GA4 BigQuery export and reports
 * the custom keys it emits. This is a BigQuery scan: it takes seconds, and the
 * backend throttles it to 6 requests per minute (429 beyond that).
 *
 * Leave `windowDays` unset unless the caller really means to override it — the
 * backend owns both the default (7) and the bounds (1–30).
 */
export async function discoverGA4CustomParams(
  id: string,
  propertyId: string,
  windowDays?: number,
): Promise<GA4DiscoveredCustomParams> {
  const q = new URLSearchParams({ property_id: propertyId });
  if (windowDays !== undefined) {
    q.set("window_days", String(windowDays));
  }
  const resp = await apiJson<{ data: GA4DiscoveredCustomParams }>(
    `/managed-sources/ga4-bq-export/${encodeURIComponent(id)}/discover-custom-params?${q.toString()}`,
    { unwrapData: false },
  );
  return resp.data;
}

// ---- Google Analytics OAuth ----

/** GET /managed-sources/google/auth-url */
export async function getGoogleAuthUrl(
  state?: string,
  connectorType?: string,
): Promise<{ url: string }> {
  const q = new URLSearchParams();
  if (state) q.set("state", state);
  if (connectorType) q.set("connector_type", connectorType);
  const qs = q.toString();
  return apiJson<{ url: string }>(
    `/managed-sources/google/auth-url${qs ? `?${qs}` : ""}`
  );
}

/**
 * POST /managed-sources/google/exchange-token
 *
 * `state` is the signed state the backend embedded in the auth URL. It is
 * optional for the GA4/Ads flows (unchanged), but REQUIRED for
 * `GOOGLE_DRIVE_CONTEXT`: the backend refuses a drive.file grant that can't
 * be tied back to the account that started the handoff (ADR-0139). Read it
 * off the `google_oauth_callback` postMessage and pass it straight through.
 */
export async function exchangeGoogleToken(
  code: string,
  name: string,
  connectorType?: string,
  state?: string,
): Promise<{
  authorization_id: string;
  name: string;
  property_count: number;
  properties: GoogleAnalyticsProperty[];
  /** Search Console only (`connectorType === "GOOGLE_SEARCH_CONSOLE"`): the
   *  backend already listed the sites to verify the grant and returns them
   *  here, so the wizard does not need a follow-up GET /google/sites. Absent
   *  for every other connector type. */
  site_count?: number;
  sites?: GoogleSearchConsoleSite[];
}> {
  return apiJson(`/managed-sources/google/exchange-token`, {
    method: "POST",
    body: JSON.stringify({
      code,
      name,
      connector_type: connectorType,
      ...(state ? { state } : {}),
    }),
  });
}

/**
 * POST /managed-sources/google/upgrade-token
 *
 * Re-consents an EXISTING Google authorization with a wider scope set,
 * updating that row in place instead of creating a new one. Used by the
 * Google Ads audience wizard's "Grant Customer Match access" action, so a
 * customer keeps ONE connection per Google account.
 *
 * The user must consent with the SAME Google account — a different account
 * re-points the connection (and the sources hanging off it). The backend
 * refuses a grant that drops scopes the row already had.
 */
export async function upgradeGoogleToken(
  code: string,
  authorizationId: string,
  connectorType?: string,
): Promise<{ authorization_id: string; name: string; scope: string }> {
  return apiJson(`/managed-sources/google/upgrade-token`, {
    method: "POST",
    body: JSON.stringify({
      code,
      authorization_id: authorizationId,
      connector_type: connectorType,
    }),
  });
}

/** GET /managed-sources/google/properties */
export async function listGAProperties(
  authorizationId: string
): Promise<{ properties: GoogleAnalyticsProperty[] }> {
  return apiJson(
    `/managed-sources/google/properties?authorization_id=${encodeURIComponent(authorizationId)}`
  );
}

/** GET /managed-sources/google/ads-customers */
export async function listGoogleAdsCustomers(
  authorizationId: string
): Promise<{ customers: GoogleAdsCustomer[] }> {
  return apiJson(
    `/managed-sources/google/ads-customers?authorization_id=${encodeURIComponent(authorizationId)}`
  );
}

/** GET /managed-sources/google/sites — Search Console properties. */
export async function listGoogleSites(
  authorizationId: string
): Promise<{ sites: GoogleSearchConsoleSite[] }> {
  return apiJson(
    `/managed-sources/google/sites?authorization_id=${encodeURIComponent(authorizationId)}`
  );
}

/** POST /managed-sources/google/test */
export async function testGoogleConnection(
  authorizationId: string,
  propertyId: string
): Promise<{
  success: boolean;
  row_count?: number;
  timezone?: string;
  currency?: string;
  display_name?: string;
}> {
  return apiJson(`/managed-sources/google/test`, {
    method: "POST",
    body: JSON.stringify({
      authorization_id: authorizationId,
      property_id: propertyId,
    }),
  });
}

/**
 * POST /managed-sources/google/test — Search Console branch.
 *
 * A separate function rather than a second argument on `testGoogleConnection`
 * because the endpoint takes EXACTLY ONE of `property_id` / `site_url` (the BFF
 * rejects both and neither with a 400), and a single optional-argument helper
 * makes it easy to send both by accident.
 *
 * `siteUrl` is passed through verbatim — the backend re-validates it against
 * the same pattern the setup route uses, and it must match the string
 * `sites.list` returned character for character.
 */
export async function testGoogleSearchConsoleConnection(
  authorizationId: string,
  siteUrl: string
): Promise<{
  success: boolean;
  row_count?: number;
  site_url?: string;
  error?: string;
}> {
  return apiJson(`/managed-sources/google/test`, {
    method: "POST",
    body: JSON.stringify({
      authorization_id: authorizationId,
      site_url: siteUrl,
    }),
  });
}

// ---- Facebook OAuth ----

/**
 * GET /managed-sources/facebook/auth-url
 *
 * State is always issued server-side — the caller stores the returned
 * `state` and echoes it back to exchangeFacebookToken so the backend
 * can verify the handoff wasn't tampered with.
 *
 * `connectorType` selects the scope-set the consent screen requests:
 * omitted/`FACEBOOK_ADS` is the plain source-connect set (`ads_read`,
 * unchanged behaviour); `FACEBOOK_ADS_AUDIENCE` additionally requests
 * `ads_management` for the Meta audience destination wizard (plan
 * 2026-07-29-meta-custom-audience-destination.md).
 */
export async function getFacebookAuthUrl(
  connectorType?: string,
): Promise<{
  url: string;
  state: string;
}> {
  const q = new URLSearchParams();
  if (connectorType) q.set("connector_type", connectorType);
  const qs = q.toString();
  return apiJson<{ url: string; state: string }>(
    `/managed-sources/facebook/auth-url${qs ? `?${qs}` : ""}`,
  );
}

/** POST /managed-sources/facebook/exchange-token */
export async function exchangeFacebookToken(
  code: string,
  name: string,
  state: string,
  connectorType?: string,
): Promise<{
  authorization_id: string;
  name: string;
  ad_accounts: FacebookAdAccount[];
}> {
  return apiJson(`/managed-sources/facebook/exchange-token`, {
    method: "POST",
    body: JSON.stringify({ code, name, state, connector_type: connectorType }),
  });
}

/** GET /managed-sources/facebook/ad-accounts */
export async function listFacebookAdAccounts(
  authorizationId: string,
): Promise<{ ad_accounts: FacebookAdAccount[] }> {
  return apiJson(
    `/managed-sources/facebook/ad-accounts?authorization_id=${encodeURIComponent(authorizationId)}`
  );
}

// ---- Microsoft Advertising OAuth ----

/**
 * GET /managed-sources/microsoft-ads/auth-url
 *
 * The backend mints AND signs the account-bound state itself (CSRF), so the
 * caller stores the returned `state` + `code_verifier` and echoes both back to
 * exchangeMicrosoftToken. The PKCE verifier rides this authenticated API — never
 * the OAuth redirect (that is the whole point of PKCE).
 *
 * `identityProvider` picks which sign-in the consent screen uses: a Microsoft
 * identity or a Google account (both are valid Microsoft Advertising logins).
 * The backend builds the matching OAuth URL and carries the choice inside the
 * signed `state`, so exchangeMicrosoftToken stays unchanged.
 */
export async function getMicrosoftAuthUrl(
  identityProvider: "microsoft" | "google",
): Promise<{
  url: string;
  state: string;
  code_verifier: string;
}> {
  const q = new URLSearchParams({ identity_provider: identityProvider });
  return apiJson(`/managed-sources/microsoft-ads/auth-url?${q.toString()}`);
}

/**
 * POST /managed-sources/microsoft-ads/exchange-token
 *
 * `state` and `code_verifier` are both required: the backend verifies the
 * account-bound signed state and completes the PKCE exchange. Read them off the
 * `microsoft_oauth_callback` postMessage (state) and the stashed auth-url
 * response (verifier).
 */
export async function exchangeMicrosoftToken(
  code: string,
  state: string,
  codeVerifier: string,
  name: string,
): Promise<{ authorization_id: string; name: string }> {
  return apiJson(`/managed-sources/microsoft-ads/exchange-token`, {
    method: "POST",
    body: JSON.stringify({ code, state, code_verifier: codeVerifier, name }),
  });
}

/** GET /managed-sources/microsoft-ads/customers — distinct manager accounts */
export async function listMicrosoftManagers(
  authorizationId: string,
): Promise<{ customers: MicrosoftAdsManager[] }> {
  return apiJson(
    `/managed-sources/microsoft-ads/customers?authorization_id=${encodeURIComponent(authorizationId)}`
  );
}

/** GET /managed-sources/microsoft-ads/accounts — advertiser accounts under one manager */
export async function listMicrosoftAccounts(
  authorizationId: string,
  customerId: string,
): Promise<{ accounts: MicrosoftAdsAccount[] }> {
  const q = new URLSearchParams({
    authorization_id: authorizationId,
    customer_id: customerId,
  });
  return apiJson(`/managed-sources/microsoft-ads/accounts?${q.toString()}`);
}

/** GET /managed-sources/:id/transforms/:transformRunId/log */
export async function getTransformLog(
  sourceId: string,
  transformRunId: string,
): Promise<{ transform_run_id: string; log_output: string }> {
  return apiJson<{ transform_run_id: string; log_output: string }>(
    `/managed-sources/${encodeURIComponent(sourceId)}/transforms/${encodeURIComponent(transformRunId)}/log`,
    { unwrapData: false },
  );
}

/** GET /managed-sources/:id/runs */
export type PipelineRunPage = {
  data: PipelineRun[];
  meta: {
    limit: number;
    offset: number;
    total: number;
    /** Pass back as `cursor` for the next page. `null` means this was the last. */
    next_cursor: string | null;
  };
};

export async function listPipelineRuns(
  id: string,
  params?: { limit?: number; offset?: number; cursor?: string | null }
): Promise<PipelineRunPage> {
  const q = new URLSearchParams();
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  // Cursor wins over offset server-side; sending both is harmless but pointless.
  if (params?.cursor) q.set("cursor", params.cursor);
  const qs = q.toString();
  return apiJson<PipelineRunPage>(
    `/managed-sources/${encodeURIComponent(id)}/runs${qs ? `?${qs}` : ""}`,
    { unwrapData: false }
  );
}
